import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "sonner";
import { Loader2, Upload, Check, FileText } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { PackageCards } from "../components/PackageCards";
import { useI18n } from "../i18n";
import { api, formatError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { EDUCATION, BANKS } from "../lib/constants";
import { usePlans } from "../lib/plans";
import { Cinematic } from "../components/Layout";

const STEPS = ["step_personal", "step_bank", "step_documents", "step_security", "step_package"];
const Field = ({ label, children }) => <div className="space-y-2"><Label>{label}</Label>{children}</div>;

const FileDrop = ({ label, hint, accept, file, onFile, testId }) => {
  const { t } = useI18n();
  const preview = file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
  return (
    <Field label={label}>
      <label className="flex h-48 cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl border border-dashed transition-colors hover:bg-accent" data-testid={`${testId}-dropzone`}>
        {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : file ? (
          <span className="flex items-center gap-2 text-sm"><FileText className="h-5 w-5" />{file.name}</span>
        ) : (
          <><Upload className="h-6 w-6" style={{ color: "var(--gold)" }} /><span className="text-sm font-medium">{t("upload_click")}</span><span className="text-xs text-muted-foreground">{hint}</span></>
        )}
        <input type="file" accept={accept} className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid={`${testId}-input`} />
      </label>
    </Field>
  );
};

const Stepper = ({ step }) => {
  const { t } = useI18n();
  return (
    <ol className="flex flex-wrap gap-2">
      {STEPS.map((s, i) => (
        <li key={s} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${i === step ? "border-[color:var(--gold)]" : ""}`} data-testid={`reg-step-${i}`}
          style={i <= step ? { color: "var(--gold)", background: i === step ? "var(--gold-soft)" : undefined } : { color: "hsl(var(--muted-foreground))" }}>
          <span className="flex h-5 w-5 items-center justify-center rounded-full border text-[10px]">{i < step ? <Check className="h-3 w-3" /> : i + 1}</span>{t(s)}
        </li>
      ))}
    </ol>
  );
};

export default function Register() {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ full_name: "", gender: "", phone: "", education: "", bank_name: "", bank_account_number: "", bank_account_name: "", password: "", confirm: "", package: "", biometric_credential_id: "" });
  const { activePlans } = usePlans();
  useEffect(() => {
    if (!f.package && activePlans.length) setF((p) => ({ ...p, package: (activePlans.find((x) => x.recommended) || activePlans[0]).key }));
  }, [activePlans, f.package]);
  const [idDoc, setIdDoc] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const validate = () => {
    if (step === 0) {
      if (!f.full_name.trim() || !f.gender || !f.education || !f.phone) return t("err_required");
      if (!isValidPhoneNumber(f.phone)) return t("err_phone");
    }
    if (step === 1 && (!f.bank_name || !f.bank_account_number.trim() || !f.bank_account_name.trim())) return t("err_required");
    if (step === 2 && (!idDoc || !photo)) return t("err_required");
    if (step === 3) {
      if (f.password.length < 6) return t("err_password_len");
      if (f.password !== f.confirm) return t("err_password_match");
    }
    return "";
  };

  const next = () => { const e = validate(); setError(e); if (!e) setStep(step + 1); };

  const submit = async () => {
    if (!f.package) { setError(t("err_required")); return; }
    setBusy(true); setError("");
    const fd = new FormData();
    ["full_name", "gender", "phone", "education", "bank_name", "bank_account_number", "bank_account_name", "password", "package"].forEach((k) => fd.append(k, f[k]));
    if (f.biometric_credential_id) fd.append("biometric_credential_id", f.biometric_credential_id);
    fd.append("id_doc", idDoc); fd.append("photo", photo);
    try {
      const { data } = await api.post("/auth/register", fd);
      signIn(data); toast.success(t("reg_success")); nav("/dashboard");
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Cinematic>
    <section className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
      <p className="eyebrow">{t("pk_eyebrow")}</p>
      <h1 className="mt-3 font-serif text-4xl font-bold sm:text-5xl" data-testid="register-title">{t("reg_title")}</h1>
      <p className="mt-3 text-muted-foreground">{t("reg_sub")} {t("reg_have_account")} <Link to="/login" style={{ color: "var(--gold)" }} className="font-semibold" data-testid="register-login-link">{t("nav_login")}</Link></p>
      <div className="mt-8"><Stepper step={step} /></div>
      <div className="lux-card mt-8 p-6 sm:p-10 fade-up" key={step} data-testid="register-form">
        {step === 0 && (
          <div className="grid gap-6 md:grid-cols-2">
            <Field label={t("f_full_name")}><Input className="h-11" value={f.full_name} onChange={set("full_name")} data-testid="reg-full-name-input" /></Field>
            <Field label={t("f_phone")}><PhoneInput international defaultCountry="ET" value={f.phone} onChange={set("phone")} data-testid="reg-phone-input" /></Field>
            <Field label={t("f_gender")}>
              <RadioGroup value={f.gender} onValueChange={set("gender")} className="flex gap-6 pt-2">
                {["male", "female", "other"].map((g) => (
                  <label key={g} className="flex cursor-pointer items-center gap-2 text-sm"><RadioGroupItem value={g} data-testid={`reg-gender-${g}`} />{t(`g_${g}`)}</label>
                ))}
              </RadioGroup>
            </Field>
            <Field label={t("f_education")}>
              <Select value={f.education} onValueChange={set("education")}>
                <SelectTrigger className="h-11" data-testid="reg-education-select"><SelectValue placeholder={t("select_placeholder")} /></SelectTrigger>
                <SelectContent>{EDUCATION.map((e) => <SelectItem key={e} value={e} data-testid={`reg-education-${e}`}>{t(`edu_${e}`)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        )}
        {step === 1 && (
          <div className="grid gap-6 md:grid-cols-2">
            <Field label={t("f_bank_name")}>
              <Select value={f.bank_name} onValueChange={set("bank_name")}>
                <SelectTrigger className="h-11" data-testid="reg-bank-select"><SelectValue placeholder={t("select_placeholder")} /></SelectTrigger>
                <SelectContent>{BANKS.map((b, i) => <SelectItem key={b} value={b} data-testid={`reg-bank-${i}`}>{b === "Other" ? t("bank_other") : b}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("f_account_number")}><Input className="h-11 font-mono" value={f.bank_account_number} onChange={set("bank_account_number")} data-testid="reg-account-number-input" /></Field>
            <Field label={t("f_account_name")}><Input className="h-11" value={f.bank_account_name} onChange={set("bank_account_name")} data-testid="reg-account-name-input" /></Field>
          </div>
        )}
        {step === 2 && (
          <div className="grid gap-6 md:grid-cols-2">
            <FileDrop label={t("f_id_upload")} hint={t("upload_hint")} accept="image/jpeg,image/png,image/webp,application/pdf" file={idDoc} onFile={setIdDoc} testId="reg-id-upload" />
            <FileDrop label={t("f_photo_upload")} hint={t("upload_photo_hint")} accept="image/jpeg,image/png,image/webp" file={photo} onFile={setPhoto} testId="reg-photo-upload" />
          </div>
        )}
        {step === 3 && (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Field label={t("f_password")}><Input type="password" className="h-11" value={f.password} onChange={set("password")} data-testid="reg-password-input" /></Field>
              <Field label={t("f_confirm_password")}><Input type="password" className="h-11" value={f.confirm} onChange={set("confirm")} data-testid="reg-confirm-password-input" /></Field>
            </div>
          </div>
        )}
        {step === 4 && (
          <div>
            <h3 className="mb-8 font-serif text-2xl font-semibold">{t("review_title")}</h3>
            <PackageCards selected={f.package} onSelect={set("package")} testPrefix="reg-package" />
          </div>
        )}
        {error && <p className="mt-6 text-sm text-destructive" data-testid="register-error">{error}</p>}
        <div className="mt-10 flex justify-between gap-4">
          <button type="button" onClick={() => { setError(""); setStep(step - 1); }} disabled={step === 0} className="btn-ghost-gold disabled:opacity-30" data-testid="reg-back-btn">{t("btn_back")}</button>
          {step < 4 ? (
            <button type="button" onClick={next} className="btn-gold" data-testid="reg-next-btn">{t("btn_next")}</button>
          ) : (
            <button type="button" onClick={submit} disabled={busy} className="btn-gold" data-testid="reg-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("btn_submit")}</button>
          )}
        </div>
      </div>
    </section>
    </Cinematic>
  );
}
